import { useEffect, useState } from "react";
import axios from "axios";
import SectionHeading from "../components/ui/SectionHeading.jsx";
import CTABanner from "../components/ui/CTABanner.jsx";
import EventCard from "../components/cards/EventCard.jsx";
import Marquee from "../components/ui/Marquee.jsx";
import { Reveal, MaskLines, ImageReveal } from "../components/motion/Reveal.jsx";
import useSeo from "../hooks/useSeo.js";
import { EVENTS_SAMPLE } from "../content.js";

const CATEGORIES = ["Fitness Events", "Sports Events", "Wellness", "Family", "Community", "Tournaments"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HIGHLIGHTS = [
  { label: "Fitness", img: "/oneiros/perform-speed.jpg" },
  { label: "Sports", img: "/oneiros/kat-badminton.jpg" },
  { label: "Families", img: "/oneiros/fac-kids-arena.jpg" },
  { label: "Celebrations", img: "/oneiros/banquet-wide.jpg" },
  { label: "Training", img: "/oneiros/strongest-gym.jpg" },
  { label: "Wellness", img: "/oneiros/fac-recovery.jpg" },
];

export default function Events() {
  const [events, setEvents] = useState(EVENTS_SAMPLE);
  useSeo({
    title: "Events & Experiences | Oneiros Sports Club Surat",
    description:
      "A calendar of sports, fitness, wellness and community experiences at Oneiros Sports Club Surat. Event schedule is being updated by the club team.",
    image: "/oneiros/kat-lifestyle.jpg",
  });

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/events`)
      .then(({ data }) => {
        if (alive && Array.isArray(data) && data.length) setEvents(data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <>
      <section className="mx-auto max-w-[1440px] px-6 pb-14 pt-36 lg:px-12 lg:pt-48">
        <SectionHeading
          eyebrow="Events & Experiences"
          lines={["More Than Fitness.", "More to Experience."]}
          copy="A calendar of sports, fitness, wellness and community experiences."
        />
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap gap-2" data-testid="events-categories">
            {CATEGORIES.map((c) => (
              <span key={c} className="rounded-full border border-hairline px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-navy/70">
                {c}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-12">
        <Reveal>
          <p data-testid="events-cms-note" className="mb-8 border border-dashed border-brand/50 bg-brand-tint px-5 py-4 text-xs leading-relaxed text-navy/70">
            <span className="font-bold uppercase tracking-[0.15em] text-brand">Sample preview —</span> the Oneiros event calendar is being
            prepared. Cards below are placeholders ready for the club team to populate with real events; no dates are invented.
          </p>
        </Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((ev) => (
            <EventCard key={ev.id} event={ev} />
          ))}
        </div>
      </section>

      <section className="border-y border-hairline bg-mist py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <SectionHeading eyebrow="The Experience" lines={["Live the", "Oneiros Experience."]} />
          <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
            {HIGHLIGHTS.map((h, i) => (
              <ImageReveal key={h.label} src={h.img} alt={`${h.label} at Oneiros`} className={`w-full ${i === 0 ? "col-span-2 aspect-[16/9] md:col-span-2" : "aspect-square"}`} delay={i * 0.05} />
            ))}
          </div>
        </div>
      </section>

      <Marquee items={["Fitness", "Sports", "Families", "Celebrations", "Training", "Wellness"]} slow />
      <div className="h-px w-full bg-navy-obsidian" />
      <CTABanner
        eyebrow="Events & Experiences"
        lines={["What Will You", "Experience Next?"]}
        primary={{ label: "Enquire Now", to: "/contact" }}
        secondary={{ label: "Explore Our Clubs", to: "/our-clubs" }}
        testId="events-final-cta"
      />
    </>
  );
}
